import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { convertToVwMob } from '../../Utils/responsive';

type Props = {
  open: boolean;
  title?: string;
  onClose?: () => void;
  variant?: 'default' | 'transparent';
  padding?: string;
  children: React.ReactNode;
};

export default function DSBottomSheet({ open, title, onClose, variant = 'default', padding, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      onPointerDown={() => {
        onClose?.();
      }}
    >
      <Sheet
        $variant={variant}
        $padding={padding}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
      >
        {title ? (
          <Header>
            <Title>{title}</Title>
            {onClose && (
              <CloseButton onClick={onClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19.3072 1.70719C19.4947 1.51965 19.6001 1.26528 19.6001 1.00005C19.6001 0.734821 19.4947 0.480454 19.3072 0.292908C19.1196 0.105362 18.8653 0 18.6 0C18.3348 0 18.0805 0.105362 17.8929 0.292908L9.80005 8.38577L1.70719 0.292908C1.51965 0.105362 1.26528 1.93113e-08 1.00005 2.12874e-08C0.734821 2.32635e-08 0.480454 0.105362 0.292908 0.292908C0.105362 0.480454 2.32635e-08 0.734821 2.12874e-08 1.00005C1.93113e-08 1.26528 0.105362 1.51965 0.292908 1.70719L8.38577 9.80005L0.292908 17.8929C0.105362 18.0805 0 18.3348 0 18.6C0 18.8653 0.105362 19.1196 0.292908 19.3072C0.480454 19.4947 0.734821 19.6001 1.00005 19.6001C1.26528 19.6001 1.51965 19.4947 1.70719 19.3072L9.80005 11.2143L17.8929 19.3072C18.0805 19.4947 18.3348 19.6001 18.6 19.6001C18.8653 19.6001 19.1196 19.4947 19.3072 19.3072C19.4947 19.1196 19.6001 18.8653 19.6001 18.6C19.6001 18.3348 19.4947 18.0805 19.3072 17.8929L11.2143 9.80005L19.3072 1.70719Z"
                    fill="#A6A6A6"
                  />
                </svg>
              </CloseButton>
            )}
          </Header>
        ) : null}
        {children}
        <SafeArea />
      </Sheet>
    </Overlay>,
    document.body,
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div<{ $variant: 'default' | 'transparent'; $padding?: string }>`
  width: 100%;
  max-width: none;
  border-radius: ${convertToVwMob(20)} ${convertToVwMob(20)} 0 0;
  background: ${({ $variant }) => ($variant === 'transparent' ? 'transparent' : '#ffffff')};
  padding: ${({ $variant, $padding }) => $padding ?? ($variant === 'transparent' ? '0' : `${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(24)}`)};
  box-shadow: ${({ $variant }) => ($variant === 'transparent' ? 'none' : '0 -4px 20px rgba(0, 0, 0, 0.1)')};
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${convertToVwMob(16)};
`;

const Title = styled.div`
  color: var(--grey-scale-90-main-text, #17171b);
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
`;

const SafeArea = styled.div`
  height: env(safe-area-inset-bottom);
  background: transparent;
`;
